/**
 * productImage — map a product NAME to its photo, served from /public.
 *
 * The catalogue photos live in `public/product-images/…` (drop the files there;
 * subfolders like `snacks/`, `dairy/`, `sanitary pads/` are preserved). Because
 * they're in /public, this is just a URL lookup — nothing is imported at build
 * time, so the app builds and runs even before the image files are added
 * ("managed later"). Unknown names return null and the caller shows the emoji.
 *
 * Matching is exact first, then a trimmed/case-insensitive fallback so the many
 * name spellings below (aliases) all resolve.
 */

// name → path relative to public/product-images/
export const PRODUCT_IMAGES = {
  'White Bread': 'white_bread_metro_gold.png',
  'Bhuja Chana': 'roasted_chana_250gm.jpg',
  'Maggie (Small)': 'maggie_small_packet.jpg',
  'Maggie (Family Pack)': 'maggie_family_pack.jpg',
  Moongfali: 'moongfali_peanut_250gm.jpg',
  'Soyabean (Packet)': 'soyachunks_fortune_packet.jpg',
  'Soyabean (Open - Small)': 'soyabean_chunks_open_250gm.jpeg',
  'Macaroni / Pasta': 'fusli_pasta_microni_250gm.jpeg',
  'Sugar (Chini)': 'suger_chini_250gm.jpg',
  'Glucose D (200g)': 'glucose_d_small.jpg',
  'Glucose D (400g)': 'glucose_d_large.jpeg',
  'Jam (Small)': 'kissan_mixed_fruit_jam_small.jpeg',
  'Jam (Big)': 'kissan_mixed_fruit_jam_large.jpeg',
  'Colgate (Small)': 'colgate_toothpaste_small.jpg',
  'Colgate (Medium)': 'colgate_toothpaste_large.webp',
  'Colgate (Big)': 'colgate_toothpaste_large.webp',
  'Closeup (Small)': 'close_up_toothpaste.jpg',
  'Closeup (Big)': 'close_up_toothpaste.jpg',
  'Sensodyne Toothpaste': 'sensodine_toothpaste.jpeg',
  'Coffee (Nescafe - packet)': 'nescafe_coffee_small_packet.webp',
  'Coffee (Nescafe - 24g)': 'nescafe_coffee_medium.jpg',
  'Coffee (Nescafe - 50g)': 'nescafe_coffee_large.jpeg',
  'Mustard Oil (Sarso Tel)': 'sarso_tel_sudhh_ghani_1kg.jpg',
  'Fortune Oil': 'sarso_tel_fortune_1kg.jpg',
  'Garam Masala (Whole)': 'khada_garam_masala_packet.jpeg',
  'Garam Masala (Packet)': 'khada_garam_masala_packet.jpeg',
  'Kacha Chana': 'kacha_chana_250gm.jpg',
  Makhana: 'makhana_100gm.jpeg',
  'Kaju (Cashew)': 'kaju_100gm.jpg',
  'Pista (Packet)': 'fresh_ pista_packet.jpg',
  'Badam (Almond)': 'badam_100gm.jpg',
  'Lassi (250ml)': 'amul_lassi_250ml_tetrapack_24rupees.jpeg',
  'Buttermilk (200ml)': 'amul_masti_spiced_buttermilk_200ml_14rupees.jpg',
  'Buttermilk (1L)': 'amul_masti_spiced_buttermilk_1lt_65rupees.jpg',
  'Practical File (Physics - 30 Pages)': 'practical_file_physics.jpg',
  'Practical File (Physics - 60 Pages)': 'practical_file_physics.jpg',
  'Practical File (Chemistry - 30 Pages)': 'practical_file_chemistry.jpg',
  'Practical File (Chemistry - 60 Pages)': 'practical_file_chemistry.jpg',
  'Practical File (General - 30 Pages)': 'practical_file_general.jpeg',
  'Practical File (General - 60 Pages)': 'practical_file_general.jpeg',
  'Drawing Book (A3 - 36 pages)': 'engineering_drawing_36page.jpg',
  'Drawing Book (A3 - 56 pages)': 'engineering_drawing_56page.jpg',
  'Pen (Blue, Cheap)': 'elkos_pen_4rupees.jpg',
  'Pen (Black, Cheap)': 'elkos_pen_4rupees.jpg',
  'Pen (Blue, Medium)': 'elkos_pen_5rupees.jpg',
  'Pen (Black, Medium)': 'elkos_pen_5rupees.jpg',
  'Pen (Blue, Good)': 'elkos_pen_10rupees.jpg',
  'Pen (Black, Good)': 'elkos_pen_10rupees.jpg',
  Pencil: 'natraj_pencil.png',
  Eraser: 'eraser_apsara.jpg',
  Sharpener: 'pencil_sharpner_plastic.jpg',
  'A4 Size Pages (500 sheets)': 'a4_size_paper_500pages.jpg',
  Stapler: 'stapler.jpg',
  'Staple Pins': 'staples_pin_box.jpeg',
  'Sticky Notes': 'sticky_notes_small_packet.jpeg',
  'Fevicol (Small)': 'fevicol_small.jpg',
  'Fevicol (Medium)': 'fevicol_small.jpg',
  'Fevicol (Big)': 'fevicol_small.jpg',
  'Double-Sided Tape (Small)': 'cello_tape_small.jpeg',
  'Double-Sided Tape (Big)': 'cello_tape_small.jpeg',
  'Spiral Notebook (Plain - 100 Pages)': 'spiral_notebook_100pg.jpg',
  'Spiral Notebook (Plain - 200 Pages)': 'spiral_notebook_200pg.jpeg',
  'Spiral Notebook (Lined - 100 Pages)': 'spiral_notebook_100pg.jpg',
  'Register (A4 Rough - 100 Pages)': 'register_a4_rough.jpg',
  'Register (A4 Rough - 200 Pages)': 'register_a4_rough_a4.jpg',
  'Graph Book': 'graph_book.jpg',
  'Cello Tape': 'cello_tape_small.jpeg',
  Highlighter: 'highlighter.jpeg',
  'Fresh Banana (high Quality)': 'fresh_bannana_500gm.webp',
  'Pomegranate (Fresh Anaar)': 'pomegranate_anar_500gm.jpg',
  Mango: 'mango_aam_500gm.jpg',
  'Fresh Apple (Good Quality)': 'fresh_apple_500gm.jpg',
  // 'Orange (Santara)': 'orange_santara_500gm.jpg',
  'Fresh Papaya (Good Quality )': 'fresh_papaya_1kg.jpg',
  Kiwi: 'fresh_kiwi_1piece.jpg',
  'Coconut (Nariyal)': 'nariyal_water.webp',
  'Grapes (Angoor)': 'fresh_angoor_250gm.jpg',
  'Eggs (30 pieces)': 'eggs_tray_30_piece_180rupees.jpg',
  'Raw Chicken (500g)': 'raw_chicken_500gm_120rupees.jpg',

  // --- GROCERRY NEW ---
  'Black Cherry Polish (54g)': 'grocerry new/black_cherry_polish_54rupees - Copy.jpg',
  'Black Cherry Polish (93g)': 'grocerry new/black_cherry_polish_93rupees.jpg',
  'Cherry Blossom Black Shoe Polish (54g)': 'grocerry new/black_cherry_polish_54rupees - Copy.jpg',
  'Cherry Blossom Black Shoe Polish (93g)': 'grocerry new/black_cherry_polish_93rupees.jpg',
  'Bournvita (200g)': 'grocerry new/bournvita_200gm_138rupees.jpg',
  'Bournvita (200gm)': 'grocerry new/bournvita_200gm_138rupees.jpg',
  'Bournvita (500g)': 'grocerry new/bournvita_500gm_263rupees.jpg',
  'Bournvita (500gm)': 'grocerry new/bournvita_500gm_263rupees.jpg',
  'Horlicks (500g)': 'grocerry new/horlicks_500gm_229rupees.webp',
  'Horlicks (500gm)': 'grocerry new/horlicks_500gm_229rupees.webp',
  'Savlon Handwash (10 Rupees)': 'grocerry new/savlon_handwash_10rupees.jpg',
  'Savlon Handwash': 'grocerry new/savlon_handwash_10rupees.jpg',
  'Yippee Noodles (10 Rupees)': 'grocerry new/yippee_maggie_10rupees.webp',
  'Yippee Maggie (10 Rupees)': 'grocerry new/yippee_maggie_10rupees.webp',
  'Yippee Noodles (5 Rupees)': 'grocerry new/yippee_maggie_5rupees - Copy.webp',
  'Yippee Maggie (5 Rupees)': 'grocerry new/yippee_maggie_5rupees - Copy.webp',

  // --- MORTINE ---
  'All Out Refill (85 Rupees)': 'mortine/all_out_ultra_fits_all_machine_refil_only_85rupees.webp',
  'All Out Ultra Refill': 'mortine/all_out_ultra_fits_all_machine_refil_only_85rupees.webp',
  'All Out Machine Set (105 Rupees)': 'mortine/all_out_ultra_fits_all_machine_set_105rupees.webp',
  'All Out Machine Set': 'mortine/all_out_ultra_fits_all_machine_set_105rupees.webp',
  'Good Knight Fast Card (10 Rupees)': 'mortine/good_night_fast_card_10rupees.webp',
  'Good Knight Fast Card': 'mortine/good_night_fast_card_10rupees.webp',
  'Good Knight Flash Refill (85 Rupees)': 'mortine/good_night_flash_mortine_refil_only_85rupees.avif',
  'Good Knight Flash Refill': 'mortine/good_night_flash_mortine_refil_only_85rupees.avif',
  'Good Knight Flash Machine Set (105 Rupees)': 'mortine/good_night_flash_mortine_set_105rupees.jpg',
  'Good Knight Flash Machine Set': 'mortine/good_night_flash_mortine_set_105rupees.jpg',
  'Maxo Genius Max Protection': 'mortine/maxo_genius_max_protection_100rupees.jpg',
  'Maxo Genius (100 Rupees)': 'mortine/maxo_genius_max_protection_100rupees.jpg',
  'Mortein Power Booster (40 Rupees)': 'mortine/mortine_power_booster_40rupees.jpg',
  'Mortein Power Booster': 'mortine/mortine_power_booster_40rupees.jpg',
  'Mortine Power Booster': 'mortine/mortine_power_booster_40rupees.jpg',
  'Naveen Relax Citronella Incense Stick': 'mortine/naveen_relax_citronella_incense_stick.jpg',
  'Citronella Incense Stick': 'mortine/naveen_relax_citronella_incense_stick.jpg',

  // --- SANITARY PADS ---
  'Sofy Antibacterial XL (14 Units)': 'sanitary pads/sofy_antibacterial_xl_14unit_140rupees.webp',
  'Sofy Antibacterial XL': 'sanitary pads/sofy_antibacterial_xl_14unit_140rupees.webp',
  'Sofy XL+ (44 Units)': 'sanitary pads/sofy_xl+_44unit_390rupees.webp',
  'Sofy XL+': 'sanitary pads/sofy_xl+_44unit_390rupees.webp',
  'Stayfree Night XXL (6 Units)': 'sanitary pads/stayfree_night_xxl_6unit_50rupees.webp',
  'Stayfree Night XXL': 'sanitary pads/stayfree_night_xxl_6unit_50rupees.webp',
  'Stayfree Regular (18 Units)': 'sanitary pads/stayfree_regular_18unit_15rupees.webp',
  'Stayfree Regular 18 Units': 'sanitary pads/stayfree_regular_18unit_15rupees.webp',
  'Stayfree Regular (6 Units)': 'sanitary pads/stayfree_regular_6unit_37rupees.webp',
  'Stayfree Regular 6 Units': 'sanitary pads/stayfree_regular_6unit_37rupees.webp',
  'Stayfree XL (12 Units)': 'sanitary pads/stayfree_xl_12unit_83rupees.webp',
  'Stayfree XL 12 Units': 'sanitary pads/stayfree_xl_12unit_83rupees.webp',
  'Stayfree XL (18 Units)': 'sanitary pads/stayfree_xl_18unit_123rupees.webp',
  'Stayfree XL 18 Units': 'sanitary pads/stayfree_xl_18unit_123rupees.webp',
  'Whisper All Night XXL (16 Units)': 'sanitary pads/whisper_all_night_xxl_16unit_150rupees.webp',
  'Whisper All Night XXL': 'sanitary pads/whisper_all_night_xxl_16unit_150rupees.webp',
  'Whisper Ultra No Gap No Leak XL (6 Units)':
    'sanitary pads/whisper_ultra_no_gap_no_leak_xl_6unit_65rupees.webp',
  'Whisper Ultra XL (6 Units)': 'sanitary pads/whisper_ultra_no_gap_no_leak_xl_6unit_65rupees.webp',
  'Whisper Ultra XL (40 Units)': 'sanitary pads/whisper_ultra_xl_40unit_320rupees.webp',
  'Whisper Ultra XL 40 Units': 'sanitary pads/whisper_ultra_xl_40unit_320rupees.webp',

  // --- SNACKS ---
  'Haldiram Aloo Bhujia (210g)': 'snacks/haldiram_aloo_bhujia_210gm_52rupees.webp',
  'Haldiram Aloo Bhujia (210gm)': 'snacks/haldiram_aloo_bhujia_210gm_52rupees.webp',
  'Haldiram Aloo Bhujia': 'snacks/haldiram_aloo_bhujia_210gm_52rupees.webp',
  'Haldiram Bhujia (400g)': 'snacks/haldiram_bhujia_400gm_114.jpg',
  'Haldiram Bhujia (400gm)': 'snacks/haldiram_bhujia_400gm_114.jpg',
  'Haldiram Bhujia Sev': 'snacks/haldiram_bhujia_400gm_114.jpg',
  'Haldiram Khatta Meetha (210g)': 'snacks/haldiram_khata_meetha_210gm_47rupees.jpg',
  'Haldiram Khatta Meetha': 'snacks/haldiram_khata_meetha_210gm_47rupees.jpg',
  'Haldiram Khata Meetha': 'snacks/haldiram_khata_meetha_210gm_47rupees.jpg',
  'Haldiram Nut Cracker (210g)': 'snacks/haldiram_nut_cracker_210gm_55rupees.webp',
  'Haldiram Nut Cracker': 'snacks/haldiram_nut_cracker_210gm_55rupees.webp',
  'Krishna Brand Poha (1kg)': 'snacks/kishna_brand_poha_1kg_120rupees.jpg',
  'Krishna Poha (1kg)': 'snacks/kishna_brand_poha_1kg_120rupees.jpg',
  'Krishna Brand Poha (500g)': 'snacks/krishna_brand_poha_500gm_65rupees.jpg',
  'Krishna Poha (500g)': 'snacks/krishna_brand_poha_500gm_65rupees.jpg',
  'Morano Aloo Bhujia (400g)': 'snacks/morano_aloo_bhujia_400gm_85rupees.png',
  'Morano Aloo Bhujia': 'snacks/morano_aloo_bhujia_400gm_85rupees.png',
  'Morano Cornflakes (400g)': 'snacks/morano_cornflakes_400gm_85rupees.jpeg',
  'Morano Cornflakes': 'snacks/morano_cornflakes_400gm_85rupees.jpeg',
  'Morano Kaju Masoor (330g)': 'snacks/morano_kaju_masoor_330gm_85rupees.jpg',
  'Morano Kaju Masoor': 'snacks/morano_kaju_masoor_330gm_85rupees.jpg',
  'Morano Makhana Mixture (330g)': 'snacks/morano_makhana_mixture_330gm_85rupees.jpeg',
  'Morano Makhana Mixture': 'snacks/morano_makhana_mixture_330gm_85rupees.jpeg',
  'Morano Navratan Mixture (400g)': 'snacks/morano_navratan_mixture_400gm_85rupees.jpg',
  'Morano Navratan Mixture': 'snacks/morano_navratan_mixture_400gm_85rupees.jpg',
  'Pan Brand Poha (500g)': 'snacks/pan_brand_poha_500gm_79rupees.jpg',
  'Pan Poha (500g)': 'snacks/pan_brand_poha_500gm_79rupees.jpg',

  // --- SOAP ---
  'Godrej No.1 Soap (Pack of 5)': 'soap/godrej_no.1_pack_of_5_120rupees.jpg',
  'Godrej No. 1 Soap (Pack of 5)': 'soap/godrej_no.1_pack_of_5_120rupees.jpg',
  'Lifebuoy Soap (Pack of 5)': 'soap/lifeboy_pack_of_5_120rupees.jpg',
  'Lifeboy Soap (Pack of 5)': 'soap/lifeboy_pack_of_5_120rupees.jpg',
  'Lux Soap (Pack of 5)': 'soap/lux_pack_of_5_140rupees.jpg',
  'Lux (Pack of 5)': 'soap/lux_pack_of_5_140rupees.jpg',
  'Santoor Soap (Pack of 4)': 'soap/santoor_pack_of_4_150rupees.jpg',
  'Santoor (Pack of 4)': 'soap/santoor_pack_of_4_150rupees.jpg',

  // --- DAIRY ---
  'Amul Moti Toned Milk (450ml)': 'dairy/amul_moti_tonned_milk_450ml_33rupees.jpg',
  'Amul Moti Toned Milk': 'dairy/amul_moti_tonned_milk_450ml_33rupees.jpg',
  'Amul Moti (450ml)': 'dairy/amul_moti_tonned_milk_450ml_33rupees.jpg',
  'Amul Taaza Homogenized Toned Milk (200ml)':
    'dairy/amul_taaza_homogenized_tonned_milk_200ml_17rupees.webp',
  'Amul Taaza Toned Milk (200ml)': 'dairy/amul_taaza_homogenized_tonned_milk_200ml_17rupees.webp',
  'Amul Taaza (200ml)': 'dairy/amul_taaza_homogenized_tonned_milk_200ml_17rupees.webp',
  'Britannia Winkin Cow Bourbon Shake': 'dairy/britannia_winkin_cow_bourbon_shake_35rupees.jpg',
  'Britannia Winkin Cow Bourbon Shake (180ml)':
    'dairy/britannia_winkin_cow_bourbon_shake_35rupees.jpg',
  'Winkin Cow Bourbon Shake': 'dairy/britannia_winkin_cow_bourbon_shake_35rupees.jpg',
  'Britannia Winkin Cow Strawberry Shake':
    'dairy/britannia_winkin_cow_strawberry_thick_shake_35rupees.jpg',
  'Winkin Cow Strawberry Shake': 'dairy/britannia_winkin_cow_strawberry_thick_shake_35rupees.jpg',
  'Britannia Winkin Cow Vanilla Shake': 'dairy/britannia_winkin_cow_vannilla_shake_35rupees.jpg',
  'Winkin Cow Vanilla Shake': 'dairy/britannia_winkin_cow_vannilla_shake_35rupees.jpg',
  'Hersheys Chocolate Milkshake': "dairy/hershey's_choclate_milkshake_35rupees.jpg",
  "Hershey's Chocolate Milkshake": "dairy/hershey's_choclate_milkshake_35rupees.jpg",
  'Hershey Chocolate Milkshake': "dairy/hershey's_choclate_milkshake_35rupees.jpg",
  'Hersheys Cookies & Cream Milkshake': "dairy/hershey's_cookies&cream_milkshake_35rupees.jpg",
  "Hershey's Cookies & Cream Milkshake": "dairy/hershey's_cookies&cream_milkshake_35rupees.jpg",
  'Hershey Cookies & Cream Milkshake': "dairy/hershey's_cookies&cream_milkshake_35rupees.jpg",
  'Hersheys Strawberry Milkshake': "dairy/hershey's_strawberry_milkshake_35rupees.jpg",
  "Hershey's Strawberry Milkshake": "dairy/hershey's_strawberry_milkshake_35rupees.jpg",
  'Hershey Strawberry Milkshake': "dairy/hershey's_strawberry_milkshake_35rupees.jpg",
  'Hersheys Vanilla Ice Cream Milkshake': "dairy/hershey's_vannilla_icecream_milkshake_35rupees.webp",
  "Hershey's Vanilla Ice Cream Milkshake":
    "dairy/hershey's_vannilla_icecream_milkshake_35rupees.webp",
  'Hershey Vanilla Ice Cream Milkshake': "dairy/hershey's_vannilla_icecream_milkshake_35rupees.webp",
  "Hershey's Vanilla Icecream Milkshake": "dairy/hershey's_vannilla_icecream_milkshake_35rupees.webp",
  'Hersheys Vanilla Icecream Milkshake': "dairy/hershey's_vannilla_icecream_milkshake_35rupees.webp",
  'Parle Smoodh Hazelnut Chocolate': 'dairy/parle_smoodh_hazelnut_choclate_10rupees.jpg',
  'Parle Smoodh Hazelnut': 'dairy/parle_smoodh_hazelnut_choclate_10rupees.jpg',
  'Smoodh Hazelnut Chocolate': 'dairy/parle_smoodh_hazelnut_choclate_10rupees.jpg',
  'Sunfeast Dark Fantasy Chocolate Shake (300ml)':
    'dairy/sunfeast_dark_fantasy_chochlate_shake_300ml_52rupees.webp',
  'Sunfeast Dark Fantasy Chocolate Shake':
    'dairy/sunfeast_dark_fantasy_chochlate_shake_300ml_52rupees.webp',
  'Dark Fantasy Chocolate Shake': 'dairy/sunfeast_dark_fantasy_chochlate_shake_300ml_52rupees.webp',

  // --- CHOCOLATES ---
  'Cadbury Chocobakers (6 Units)': 'chochlate-chocofills/cadbury_chocobakers_6unit_60rupees.webp',
  'Cadbury Chocobakers': 'chochlate-chocofills/cadbury_chocobakers_6unit_60rupees.webp',
  Chocobakers: 'chochlate-chocofills/cadbury_chocobakers_6unit_60rupees.webp',
  'Hide & Seek Choco Fills (200g)': 'chochlate-chocofills/hide_&_seek_choco_fills_200g_95rupees.webp',
  'Hide & Seek Choco Fills': 'chochlate-chocofills/hide_&_seek_choco_fills_200g_95rupees.webp',
  'Choco Fills (200g)': 'chochlate-chocofills/hide_&_seek_choco_fills_200g_95rupees.webp',
  'Orion Choco Pie (12 Units)': 'chochlate-chocofills/orion_choco_pie_12unit_110rupees.jpg',
  'Orion Choco Pie': 'chochlate-chocofills/orion_choco_pie_12unit_110rupees.jpg',
  'Choco Pie': 'chochlate-chocofills/orion_choco_pie_12unit_110rupees.jpg',
  'Sunfeast Dark Fantasy Choco Fills (230g)':
    'chochlate-chocofills/sunfeast_dark_fantasy_choco_fills_230g_110rupees.webp',
  'Sunfeast Dark Fantasy Choco Fills':
    'chochlate-chocofills/sunfeast_dark_fantasy_choco_fills_230g_110rupees.webp',
  'Dark Fantasy Choco Fills':
    'chochlate-chocofills/sunfeast_dark_fantasy_choco_fills_230g_110rupees.webp',

  // --- CHOCOLATES NEW ---
  'Bauli Moonfills Veg Choco Creme (45g)':
    'choclate new/bauli_moonfills_veg_choco_creme_45g_22rupees.jpg',
  'Britannia Treat Cocoa Creme Croissant (45g)':
    'choclate new/britannia_treat_cocoa_creme_croissant_45g_20rupees.jpg',
  'Britannia Treat Mixed Fruit Croissant (45g)':
    'choclate new/britannia_treat_mixed_fruit_croissant_45g_20rupees.webp',
  'Britannia Treat Vanilla Creme Croissant (45g)':
    'choclate new/britannia_treat_vanilla_creme_croissant_45g_20rupees.webp',

  // --- DAIRY NEW ---
  'Amul Masti Dahi (10 Rupees)': 'dairynew/amul_masti_dahi_10rupees.webp',
  'Amul Masti Dahi (35 Rupees)': 'dairynew/amul_masti_dahi_35rupees.webp',

  // --- SNACKS NEW ---
  'Britannia Bourbon The Original Choclate Biscuits (50g)':
    'snacksnew/britannia_bourbon_the_origional_choclate_biscuits_50g_10rupees.webp',
  'Britannia Good Day Cashew Cookies (82.7g)':
    'snacksnew/britannia_good_day_cashew_coockies_82.7g_20rupees.webp',
  'Kurkure Masala Munch Namkeen (75g)':
    'snacksnew/kurkure_masala_munch_namkeen_75g_20rupees.webp',
  'Kurkure Yummy Cheese Puffcorn (52g)':
    'snacksnew/kurkure_yummy_cheese_puffcorn_52g_20rupees.avif',
  'Lays American Style Cream & Onion Potato Chips (48g)':
    'snacksnew/lays_american_style_cream&onion_potato_chips_48g_25rupees.webp',
  'Lays American Style Magic Masala Potato Chips (48g)':
    'snacksnew/lays_american_style_magic_mashala_potato_chips_48g_25rupees.webp',
  'Parle Hide & Seek Choclate Biscuit (30 Rupees)':
    'snacksnew/parle_hide_&_seek_choclate_buiscuit_30rupees.jpg',
  'Uncle Chips Plain Salted Potato Chips (20 Rupees)':
    'snacksnew/uncle_chipps_plain_salted_potato_chipps_20rupees.webp',
  'Uncle Chips Spicy Treat Potato Chips (20 Rupees)':
    'snacksnew/uncle_chipps_spicy_treat_potato_chipps_20rupees.webp',
};

/** Where the files live under /public. */
const BASE = `${import.meta.env.BASE_URL}product-images/`;

// Trimmed, lower-cased index so alias/spacing/case differences still resolve.
const NORMALIZED = Object.fromEntries(
  Object.entries(PRODUCT_IMAGES).map(([key, value]) => [key.trim().toLowerCase(), value]),
);

/**
 * Resolve a product name to a public image URL, or null if we have no mapping.
 * Path segments are URL-encoded so spaces / `&` / `+` in filenames work.
 * @param {string} name
 * @returns {string|null}
 */
export function productImage(name) {
  if (!name) return null;
  const path = PRODUCT_IMAGES[name] ?? NORMALIZED[name.trim().toLowerCase()];
  if (!path) return null;
  const encoded = path.split('/').map(encodeURIComponent).join('/');
  return `${BASE}${encoded}`;
}

export default productImage;
